import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission, useHasRole } from '../../hooks/usePermission';
import { Spinner } from './Spinner';
import type { Permission } from '../../types/permissions';

interface ProtectedRouteProps {
  /** If set, user must hold this permission to access the route. */
  permission?: Permission;
  /** If set, user must hold this role to access the route. */
  role?: string;
  children: ReactNode;
}

/**
 * Route guard that redirects unauthenticated users to login and
 * optionally checks for a required permission or role.
 *
 * When the user is authenticated but lacks the required permission/role,
 * they are redirected to the dashboard root with an "unauthorized" flag.
 */
export function ProtectedRoute({
  permission,
  role,
  children,
}: ProtectedRouteProps) {
  const { user, adminUser, loading, authError } = useAuth();
  const location = useLocation();

  const hasPermissionResult = usePermission(
    (permission ?? 'system:full_access') as Permission,
  );
  const hasRoleResult = useHasRole(role ?? '');

  if (loading) {
    return <Spinner size="lg" label="Loading..." className="min-h-screen" />;
  }

  // Not authenticated -> redirect to login
  if (!user || authError) {
    const returnUrl = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
        replace
      />
    );
  }

  // Activation gate: sales-assisted (payment setup) or self-serve draft (abandoned onboarding)
  if (adminUser?.activation?.requiresActivation) {
    const targetPath = adminUser.activation.nextRoute ?? '/activate-account';
    const activationExemptPaths = [targetPath, '/account/security', '/login'];
    if (!activationExemptPaths.includes(location.pathname) && !location.pathname.includes('/setup')) {
      return <Navigate to={targetPath} replace />;
    }
  }

  // Hard 2FA gate: email/password users whose role requires 2FA must enroll before proceeding
  const twoFaExemptPaths = ['/account/security', '/login', '/getting-started', '/activate-account'];
  if (
    adminUser &&
    adminUser.needs2FASetup &&
    !adminUser.signInProvider?.startsWith('google.com') &&
    !adminUser.signInProvider?.startsWith('microsoft.com') &&
    !adminUser.signInProvider?.startsWith('apple.com') &&
    !twoFaExemptPaths.includes(location.pathname)
  ) {
    return <Navigate to="/account/security" replace />;
  }

  // Zero-membership non-super-admin: redirect to self-serve bootstrap
  // (except if already on /getting-started or /activate-account)
  const bootstrapPaths = ['/getting-started', '/activate-account'];
  if (
    adminUser &&
    adminUser.memberships.length === 0 &&
    !adminUser.isSuperAdmin &&
    !bootstrapPaths.includes(location.pathname)
  ) {
    return <Navigate to="/getting-started" replace />;
  }

  // Authenticated but missing required permission or role -> redirect to dashboard
  const permissionOk = permission ? hasPermissionResult : true;
  const roleOk = role ? hasRoleResult : true;

  if (!permissionOk || !roleOk) {
    return <Navigate to="/?unauthorized=true" replace />;
  }

  return <>{children}</>;
}
